class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    // Same 800ms debounce as before, but cancellable so "Clear" can drop a pending submit.
    this.debouncedOnSubmit = (event) => {
      clearTimeout(this.pendingSubmit);
      this.pendingSubmit = setTimeout(() => this.onSubmitHandler(event), 800);
    };

    const facetForm = this.querySelector("form");
    facetForm.addEventListener("input", this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static cancelPendingSubmits() {
    document.querySelectorAll("facet-filters-form").forEach((form) => {
      clearTimeout(form.pendingSubmit);
    });
  }

  static clearLoadingState() {
    document
      .querySelectorAll(
        ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
      )
      .forEach((spinner) => spinner.classList.add("hidden"));
    document
      .querySelectorAll("#ProductGridContainer .collection, #ProductCount, #ProductCountDesktop")
      .forEach((element) => element.classList.remove("loading"));
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    // Only the most recent request may paint the page: abort anything still in flight.
    if (FacetFiltersForm.abortController) FacetFiltersForm.abortController.abort();
    FacetFiltersForm.abortController = new AbortController();
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById(
      "ProductCountDesktop"
    );
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove("hidden"));
    document
      .getElementById("ProductGridContainer")
      .querySelector(".collection")
      .classList.add("loading");
    if (countContainer) {
      countContainer.classList.add("loading");
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add("loading");
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    const { signal } = FacetFiltersForm.abortController;
    fetch(url, { signal })
      .then((response) => response.text())
      .then((responseText) => {
        if (signal.aborted) return;
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === "function")
          initializeScrollAnimationTrigger(html.innerHTML);

        addListenersToSortToggle();
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        // Section fetch failed or returned unexpected HTML: fall back to a full page load.
        console.error("Facets: section render failed, reloading page.", error);
        FacetFiltersForm.clearLoadingState();
        const searchParams = FacetFiltersForm.searchParamsPrev;
        window.location.href = `${window.location.pathname}${searchParams ? `?${searchParams}` : ""}`;
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === "function")
      initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById("ProductGridContainer").innerHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductGridContainer").innerHTML;

    document
      .getElementById("ProductGridContainer")
      .querySelectorAll(".scroll-trigger")
      .forEach((element) => {
        element.classList.add("scroll-trigger--cancel");
      });
  }

  static renderProductCount(html) {
    if (
      !new DOMParser()
        .parseFromString(html, "text/html")
        .getElementById("ProductCountDesktop")
    ) {
      return;
    }
    const count = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductCountDesktop")?.innerHTML;

    const container = document.getElementById("ProductCount");
    const containerDesktop = document.getElementById("ProductCountDesktop");
    container.innerHTML = count;
    container.classList.remove("loading");

    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove("loading");
    }
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add("hidden"));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (
        !Array.from(facetDetailsElementsFromFetch).some(
          ({ id }) => currentElement.id === id
        )
      ) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter(
      (element) => !matchesId(element)
    );
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(
      matchesId
    );

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML =
          elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } =
            facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document
            .querySelector(`#${elementToRender.parentElement.id} .js-filter`)
            .before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest(".js-filter").id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(
          countsToRender,
          event.target.closest(".js-filter")
        );
        FacetFiltersForm.renderMobileCounts(
          countsToRender,
          document.getElementById(closestJSFilterID)
        );

        const newFacetDetailsElement =
          document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains(
          "mobile-facets__details"
        )
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate =
          newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute("type") === "text";

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      ".active-facets-mobile",
      ".active-facets-desktop",
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      ".mobile-facets__open",
      ".mobile-facets__count",
      ".sorting",
    ];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML =
        html.querySelector(selector).innerHTML;
    });

    // Keep every "(n)" active-filter counter in sync: PLP button, drawer header, desktop title.
    const activeCountFromFetch = html.querySelector(".js-facets-active-count");
    if (activeCountFromFetch) {
      document
        .querySelectorAll(".js-facets-active-count")
        .forEach((element) => {
          element.textContent = activeCountFromFetch.textContent;
        });
    }

    if (!document.getElementById("FacetFiltersFormMobile")) {
      return;
    }

    document
      .getElementById("FacetFiltersFormMobile")
      .closest("menu-drawer")
      .bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector(".facets__summary");
    const sourceSummary = source.querySelector(".facets__summary");

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector(".facets__header");
    const sourceHeaderElement = source.querySelector(".facets__header");

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector(".facets-wrap");
    const sourceWrapElement = source.querySelector(".facets-wrap");

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(
        target.querySelector("show-more-button .label-show-more.hidden")
      );
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll(".facets__item.hidden")
          .forEach((hiddenItem) =>
            hiddenItem.classList.replace("hidden", "show-more-item")
          );
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector(".mobile-facets__list");
    const sourceFacetsList = source.querySelector(".mobile-facets__list");

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll(
      "facet-filters-form form"
    );
    const eventTargetValue = event.target.value;
    const eventTargetName = event.target.name;
    if (event.srcElement.className == "mobile-facets__checkbox") {
      const searchParams = this.createSearchParams(
        event.target.closest("form")
      );
      this.onSubmitForm(searchParams, event);
    } else {
      let forms = [];
      const isMobile =
        event.target.closest("form").id === "FacetFiltersFormMobile";

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (
            form.id === "FacetSortForm" ||
            form.id === "FacetFiltersForm" ||
            form.id === "FacetSortDrawerForm"
          ) {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === "FacetFiltersFormMobile") {
          forms.push(this.createSearchParams(form));
        }
      });
      // Clear sort_by comming from mobile
      forms = forms
        .filter((value, index) => {
          if (value.includes("sort_by") && index != 0) {
            return false;
          }
          return true;
        })
        .map((value) => {
          if (value.includes("sort_by") && eventTargetName === "sort_by") {
            return "sort_by=" + eventTargetValue;
          }
          return value;
        });
      // Industrial pages allow one body color at a time without clearing other facets.
      if (
        window.location.href.includes("industrial") &&
        eventTargetName === "filter.v.m.custom.body_color"
      ) {
        const searchParams = new URLSearchParams(forms.join("&"));
        searchParams.delete(eventTargetName);

        if (event.target.checked) {
          searchParams.append(eventTargetName, eventTargetValue);
        }

        this.onSubmitForm(searchParams.toString(), event);
        return;
      }
      this.onSubmitForm(forms.join("&"), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.cancelPendingSubmits();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) => {
      element.addEventListener("input", this.onRangeInput.bind(this));
      element.addEventListener("change", this.onRangeChange.bind(this));
      element.addEventListener("keydown", this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeInput(event) {
    if (event.target.type === "checkbox") {
      const toggle = !event.target.checked;
      this.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
        checkbox.checked = false;
      });
      if (toggle) {
        this.querySelector("#Filter-Price-GTE").value = "";
        this.querySelector("#Filter-Price-LTE").value = "";
        return;
      }
      event.target.checked = true;
      const min = parseInt(event.target.dataset.min);
      const max = parseInt(event.target.dataset.max);
      this.querySelector("#Filter-Price-GTE").value = min || "";
      this.querySelector("#Filter-Price-LTE").value = max || "";
    }
  }

  onRangeChange(event) {
    if (event.target.type === "checkbox") {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const minInput = this.querySelector("#Filter-Price-GTE");
    const maxInput = this.querySelector("#Filter-Price-LTE");
    if (maxInput.value) minInput.setAttribute("data-max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("data-min", minInput.value);
    if (minInput.value === "") maxInput.setAttribute("data-min", 0);
    if (maxInput.value === "")
      minInput.setAttribute("data-max", maxInput.getAttribute("data-max"));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("data-min"));
    const max = Number(input.getAttribute("data-max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);

const showMenu = (event) => {
  event.preventDefault();
  event.stopPropagation();
  const target = event.currentTarget;
  const select = target.closest(".select");
  const dropdownMenu = select.querySelector(".sc-sort-menu");
  const isMenuOpen = dropdownMenu.style.display === "block";
  dropdownMenu.style.display = isMenuOpen ? "none" : "block";
  target.querySelector(".arrow").classList.toggle("rotate", !isMenuOpen);
  return false;
};

const pickItem = (event) => {
  event.preventDefault();
  event.stopPropagation();
  const target = event.currentTarget;
  const select = target.closest(".select");
  const buttonText = select.querySelector(".button-text");
  const arrow = select.querySelector(".arrow");
  const dropdownMenu = select.querySelector(".sc-sort-menu");
  const input = select.querySelector('[name="sort_by"]');

  buttonText.textContent = target.textContent;
  dropdownMenu.style.display = "none";
  arrow.classList.remove("rotate");

  input.value = target.dataset.value;
  const inputEvent = new Event("input", {
    bubbles: true,
    cancelable: true,
  });
  input.dispatchEvent(inputEvent);
};

const addListenersToSortToggle = function () {
  const dropdownButtons = document.querySelectorAll(".sc-sort-toggle");

  dropdownButtons.forEach((dropdownButton) => {
    const items = dropdownButton.parentNode.querySelectorAll(".sc-sort-item");

    dropdownButton.removeEventListener("click", showMenu);
    dropdownButton.addEventListener("click", showMenu);

    items.forEach((item) => {
      item.removeEventListener("click", pickItem);
      item.addEventListener("click", pickItem);
    });
  });

  document.addEventListener("click", (event) => {
    dropdownButtons.forEach((dropdownButton) => {
      const arrow = dropdownButton.querySelector(".arrow");
      const dropdownMenu =
        dropdownButton.parentNode.querySelector(".sc-sort-menu");
      if (
        !dropdownButton.contains(event.target) &&
        !dropdownMenu.contains(event.target)
      ) {
        dropdownMenu.style.display = "none";
        arrow.classList.remove("rotate");
      }
    });
  });
};

document.addEventListener("DOMContentLoaded", () => {
  addListenersToSortToggle();
});

function highlightItem(selectedItem) {
  var items = document.querySelectorAll(".sc-sort-item");
  items.forEach(function (item) {
    item.classList.remove("active");
  });
  selectedItem.classList.add("active");
}
